import { StateCreator } from 'zustand';
import { nanoid } from 'nanoid';
import type {
  Character,
  Characteristics,
  Currency,
  Rank,
  RankHistoryEntry,
  Skill,
  Talent,
  Trait,
  XPTransaction
} from '../../../types/character';
import type { Advancement } from '../../../types/advancement';
import { SkillTraining, SkillCategory } from '../../../types/character';
import { gameDataService } from '../../../services/GameDataService';
import { calculateDerivedStats, initializeProgression } from '../utils/calculations';
import { TalentsSlice } from './createTalentsSlice';
import { RankService } from '../../../services/RankService';
import { calculateEliteAdvanceCost } from '../../../utils/eliteAdvanceCost';
import { createSkillPurchase } from '../../../utils/purchaseRecordHelpers';

export interface AdvancementSlice {
  purchaseAdvancement: (advancement: Advancement, cost: number) => void;
  updateExperience: (experience: Partial<Character['experience']>) => void;
  updateCurrency: (currency: Partial<Currency>) => void;
  addThrones: (amount: number) => void;
  spendThrones: (amount: number) => boolean;
}

interface CharacterStore extends AdvancementSlice, TalentsSlice {
  character: Character | null;
  hasUnsavedChanges: boolean;
}

export const createAdvancementSlice: StateCreator<CharacterStore, [], [], AdvancementSlice> = (
  set,
  get
) => ({
  purchaseAdvancement: (advancement: Advancement, cost: number) => {
    const { character } = get();
    if (!character) return;

    const experience = character.experience || { total: 0, spent: 0, available: 0 };

    // Check if this is an elite advance (from a skipped rank)
    const skippedRanks: Rank[] = character.rankTracking?.skippedRanks ?? [];
    const advancementRank = (advancement as any).rank as Rank | undefined;
    const isEliteAdvance = advancementRank !== undefined && skippedRanks.includes(advancementRank);

    // Calculate the effective cost using the active elite advance cost mode
    const baseCost = (advancement as any).cost || cost;
    const effectiveCost = isEliteAdvance
      ? Math.max(cost, calculateEliteAdvanceCost(baseCost))
      : cost;

    // XP validation: ensure sufficient available XP (Requirement 7.4)
    if (experience.available < effectiveCost) {
      console.warn(
        `[XP Validation] Insufficient XP for advancement "${advancement.name}". ` +
          `Required: ${effectiveCost}${isEliteAdvance ? ' (Elite Advance: 2x cost)' : ''}, Available: ${experience.available}`
      );
      return;
    }

    const updatedCharacter = { ...character };

    // Store previous rank for comparison
    const previousSpentXP = experience.spent;
    const previousRank = RankService.calculateRank(previousSpentXP);

    // Update XP - use effectiveCost for elite advances
    const newSpentXP = experience.spent + effectiveCost;
    const newAvailableXP = experience.available - effectiveCost;
    updatedCharacter.experience = {
      ...experience,
      spent: newSpentXP,
      available: newAvailableXP,
      transactions: experience.transactions || []
    };

    // Recalculate rank after XP update (Requirements: 1.4, 7.3)
    const newRank = RankService.calculateRank(newSpentXP);

    // Initialize rank tracking if not present
    if (!updatedCharacter.rankTracking) {
      updatedCharacter.rankTracking = {
        currentRank: previousRank,
        highestRankAchieved: previousRank,
        activeCareerRank: previousRank, // Initialize activeCareerRank to match currentRank
        rankHistory: [
          {
            rank: previousRank,
            achievedAt: new Date(),
            spentXPAtAchievement: previousSpentXP
          }
        ]
      };
    }

    // Update current rank (Requirements: 6.1)
    updatedCharacter.rankTracking = {
      ...updatedCharacter.rankTracking,
      currentRank: newRank
    };

    // If rank increased, update highestRankAchieved and add to rankHistory (Requirements: 6.2)
    if (newRank > previousRank) {
      const newHighestRank = Math.max(
        newRank,
        updatedCharacter.rankTracking.highestRankAchieved
      ) as Rank;
      const newHistoryEntry: RankHistoryEntry = {
        rank: newRank,
        achievedAt: new Date(),
        spentXPAtAchievement: newSpentXP
      };

      updatedCharacter.rankTracking = {
        ...updatedCharacter.rankTracking,
        highestRankAchieved: newHighestRank,
        rankHistory: [...updatedCharacter.rankTracking.rankHistory, newHistoryEntry]
      };
    }

    // Create XP transaction entry (Requirements: 9.1, 9.2)
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const advAny = advancement as any;
    const advancementName = advAny.baseName || advAny.name || 'Unknown Advancement';
    const transaction: XPTransaction = {
      id: nanoid(),
      type: 'advancement',
      amount: -effectiveCost, // Negative because XP is spent (uses effectiveCost for elite advances)
      description: isEliteAdvance ? `${advancementName} (Elite Advance)` : advancementName,
      timestamp: new Date(),
      resultingSpentXP: newSpentXP,
      resultingRank: newRank
    };

    updatedCharacter.experience = {
      ...updatedCharacter.experience,
      transactions: [...(updatedCharacter.experience.transactions || []), transaction]
    };

    // Apply the advancement based on type
    // eslint-disable-next-line @typescript-eslint/no-explicit-any

    console.log('Purchasing advancement:', advAny);

    if (advAny.category === 'characteristic' || advAny.isCharacteristic) {
      // Characteristic should be stored in structured field, not parsed from name
      // Use the characteristic field directly if available
      const charKeyMap: Record<string, keyof Characteristics> = {
        'Weapon Skill': 'weaponSkill',
        'Ballistic Skill': 'ballisticSkill',
        Strength: 'strength',
        Toughness: 'toughness',
        Agility: 'agility',
        Intelligence: 'intelligence',
        Perception: 'perception',
        Willpower: 'willpower',
        Fellowship: 'fellowship',
        Influence: 'influence'
      };

      // Use characteristic field if available, otherwise skip
      const charName = (advAny as any).characteristic;
      if (charName && charKeyMap[charName]) {
        const charKey = charKeyMap[charName];
        if (charKey) {
          const increase = parseInt(advAny.modifier || '5');
          const currentValue = updatedCharacter.characteristics[charKey] || 0;
          updatedCharacter.characteristics = {
            ...updatedCharacter.characteristics,
            [charKey]: currentValue + increase
          };

          // Recalculate derived stats
          updatedCharacter.derivedStats = calculateDerivedStats(updatedCharacter);
          console.log(`Increased ${charKey} by ${increase}`);
        }
      }
    } else if (advAny.category === 'skill') {
      // Add or upgrade skill

      // Get the bonus from the advancement effect (0 for basic, 10 for +10, 20 for +20, etc.)
      const advancementBonus = advAny.effect?.bonus ?? 0;

      // Get skill ID from advancement effect - use skillId field (not skill)
      const skillId = advAny.effect?.skillId;

      // Primary lookup only - no name-based fallback
      const skillData = skillId ? gameDataService.getSkillByStringId(skillId) : undefined;

      if (!skillData) {
        console.warn(`Skill advancement missing valid skillId: ${advAny.id}`);
        return; // Don't proceed without valid skill data
      }

      // Find existing skill by ID only
      const existingSkillIndex = updatedCharacter.skills.findIndex((s) => s.id === skillId);

      if (existingSkillIndex >= 0) {
        // Upgrade existing skill training level
        const currentSkill = updatedCharacter.skills[existingSkillIndex];

        // Determine the new training level based on the advancement bonus
        let newTraining: SkillTraining;
        if (advancementBonus >= 20) {
          newTraining = SkillTraining.Adept;
        } else if (advancementBonus >= 10) {
          newTraining = SkillTraining.Experienced;
        } else {
          // This shouldn't happen for upgrades, but handle it gracefully
          newTraining = SkillTraining.Trained;
        }

        // Requirements 3.2, 3.3, 3.4: Append to purchaseHistory when upgrading skill
        const skillPurchaseEntry = createSkillPurchase(
          advAny.id,
          updatedCharacter.rankTracking?.activeCareerRank ?? 1,
          advancementBonus
        );

        updatedCharacter.skills[existingSkillIndex] = {
          ...currentSkill,
          training: newTraining,
          purchaseHistory: [...(currentSkill.purchaseHistory || []), skillPurchaseEntry]
        };
        console.log(
          `Upgraded skill ${skillData.name} to ${newTraining} with bonus ${advancementBonus}`
        );
      } else {
        // Add new skill - use skill data from GameDataService
        // Requirements 3.1, 3.3, 3.4: Initialize purchaseHistory with the advancement ID, timestamp, and rank
        // New skills start at Trained (bonus: 0 for basic skill, or the advancement bonus if specified)
        const skillPurchaseEntry = createSkillPurchase(
          advAny.id,
          updatedCharacter.rankTracking?.activeCareerRank ?? 1,
          advancementBonus
        );

        const newSkill: Skill = {
          id: skillData.id,
          name: skillData.name,
          characteristic: skillData.characteristic,
          category: (skillData.category as SkillCategory) || SkillCategory.Knowledge,
          training: SkillTraining.Trained,
          isSpecialist: skillData.isSpecialist || false,
          baseSkill: skillData.baseSkill,
          specialization: skillData.specialization,
          shortDescription: skillData.shortDescription || '',
          rulebook: skillData.rulebook || 'core',
          purchaseHistory: [skillPurchaseEntry]
        };
        updatedCharacter.skills = [...updatedCharacter.skills, newSkill];
        console.log(`Added new skill: ${skillData.name}`);
      }
    } else if (advAny.effect?.traitId) {
      // Trait advancements. Keyed on effect.traitId rather than on a category,
      // because no advance carries category 'trait' and the talent arm below
      // would otherwise swallow it: its name-derived fallback would synthesise a
      // fake talent from the advance name, which is what "Unnatural Perception
      // (x2)" used to produce.
      const traitId = advAny.effect.traitId as string;
      const traitData = gameDataService.getTrait(traitId);

      if (!traitData) {
        console.warn(`Trait advancement references an unknown trait: ${traitId}`);
        return;
      }

      // An escalating trait, e.g. Unnatural Perception (x2) then (x3), upgrades
      // the level in place rather than appending a second copy. Mirrors
      // updateMachineTrait, which does the same for Machine (N).
      const multiplier = advAny.effect.traitMultiplier as number | undefined;
      const granted: Trait = multiplier
        ? {
            ...traitData,
            name: `${traitData.name} (x${multiplier})`,
            parameters: { ...traitData.parameters, multiplier },
            mechanics: traitData.mechanics?.unnaturalCharacteristic
              ? {
                  ...traitData.mechanics,
                  unnaturalCharacteristic: {
                    ...traitData.mechanics.unnaturalCharacteristic,
                    multiplier
                  }
                }
              : traitData.mechanics
          }
        : traitData;

      const existingIndex = updatedCharacter.traits.findIndex((t) => t.id === traitId);
      if (existingIndex >= 0) {
        const updatedTraits = [...updatedCharacter.traits];
        updatedTraits[existingIndex] = granted;
        updatedCharacter.traits = updatedTraits;
        console.log(`Upgraded trait: ${granted.name}`);
      } else {
        updatedCharacter.traits = [...updatedCharacter.traits, granted];
        console.log(`Added trait: ${granted.name}`);
      }
    } else if (advAny.category === 'talent') {
      // Get talent data from GameDataService using string ID
      // Requirements 6.2, 6.4: Use ID-based lookup only, no name-based fallback
      const talentId =
        advAny.effect?.talentId ||
        (advAny.baseName || advAny.name)
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '');
      const talentData = gameDataService.getTalentByStringId(talentId);

      // Save XP/rank/transaction changes BEFORE calling addTalent,
      // since addTalent reads from get().character and does its own set()
      set({
        character: {
          ...updatedCharacter,
          lastModified: new Date()
        },
        hasUnsavedChanges: true
      });

      if (talentData) {
        // Create talent - use string id from talent data for proper identification
        // Requirements 1.2, 5.2, 5.4, 5.6: Use string ID and initialize purchaseCount and purchaseHistory as required fields
        const talentWithId: Talent = {
          ...talentData,
          id: talentData.id, // Use the string ID from talent data, not nanoid()
          purchaseCount: talentData.purchaseCount ?? 0, // Will be incremented by addTalent
          purchaseHistory: talentData.purchaseHistory ?? [] // Will be updated by addTalent
        };
        // Talented carries the affected skill on the advance, so a purchase that
        // skipped the picker (because the rulebook names the skill) still
        // records which skill the +10 applies to.
        get().addTalent(talentWithId, advAny.id, advAny.effect?.affectedSkillId);
        console.log(`Added/incremented talent: ${talentData.name}`);
      } else {
        // Fallback: create basic talent with kebab-case string ID
        // Requirements 1.2, 5.2, 5.4, 5.6: Use string ID and initialize purchaseCount and purchaseHistory as required fields
        const talentName = advAny.baseName || advAny.name;
        const talentId = talentName
          .toLowerCase()
          .replace(/\s+/g, '-')
          .replace(/[^a-z0-9-]/g, '');
        const newTalent: Talent = {
          id: talentId, // Use kebab-case string ID, not nanoid()
          name: talentName,
          tier: 1,
          prerequisites: [],
          shortDescription: advAny.shortDescription || advAny.description || '',
          fullDescription: advAny.fullDescription || advAny.description || '',
          rulebook: 'core',
          // Preserve baseTalent and specialization for proficiency tracking
          baseTalent: advAny.baseTalent,
          specialization: advAny.specialization,
          purchaseCount: 0, // Will be incremented by addTalent
          purchaseHistory: [] // Will be updated by addTalent
        };
        get().addTalent(newTalent, advAny.id);
        console.log(`Added new talent: ${talentName}`);
      }

      // Update the character reference since addTalent modifies it
      return; // Exit early since addTalent handles the state update
    }

    set({
      character: {
        ...updatedCharacter,
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  updateExperience: (experience: Partial<Character['experience']>) => {
    const { character } = get();
    if (!character) return;

    const updatedExperience = {
      ...character.experience,
      ...experience
    };

    // Ensure experience consistency
    if (updatedExperience.total < updatedExperience.spent) {
      updatedExperience.total = updatedExperience.spent;
    }
    updatedExperience.available = updatedExperience.total - updatedExperience.spent;

    set({
      character: {
        ...character,
        experience: updatedExperience,
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  updateCurrency: (currency: Partial<Currency>) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          currency: {
            ...progression.currency,
            ...currency
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  addThrones: (amount: number) => {
    const { character } = get();
    if (!character) return;

    const progression = character.progression || initializeProgression(character);

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          currency: {
            thrones: progression.currency.thrones + amount
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });
  },

  spendThrones: (amount: number): boolean => {
    const { character } = get();
    if (!character) return false;

    const progression = character.progression || initializeProgression(character);

    // Validate: prevent negative currency
    if (progression.currency.thrones < amount) {
      return false;
    }

    set({
      character: {
        ...character,
        progression: {
          ...progression,
          currency: {
            thrones: progression.currency.thrones - amount
          }
        },
        lastModified: new Date()
      },
      hasUnsavedChanges: true
    });

    return true;
  }
});
